import { Box, Button, Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { saveAs } from "file-saver";
import { fetchMeetingRecordings } from "../../../apis/meeting-recordings.api";
import MeetingRecordingsFilters, {
  MeetingRecordingsFiltersPayload,
} from "./MeetingRecordingsFilters";
import RecordingList, { RecordingCardPrimaryParty } from "./RecordingList";
import { showNotification } from "../../../services/utils";
import {
  buildConsolidatedStatementPdfForList,
} from "./pdf/consolidated-statement";
import type {
  ConsolidatedStatementViewerRole,
  PdfScopeBlock,
} from "./pdf/consolidated-statement";

export interface MeetingRecordingsViewProps {
  /** Show recordings for this vendor client; optional participant filter = ambassador. */
  lockedClientId?: string;
  /** Show recordings for this ambassador; optional participant filter = client. */
  lockedAmbassadorId?: string;
  pdfScope?: PdfScopeBlock | null;
}

export default function MeetingRecordingsView({
  lockedClientId,
  lockedAmbassadorId,
  pdfScope,
}: MeetingRecordingsViewProps) {
  const forClientDetail = Boolean(lockedClientId);
  const lockedReady =
    Boolean(lockedClientId) ||
    Boolean(lockedAmbassadorId);

  const filterVariant = forClientDetail ? "forClient" : "forAmbassador";

  const cardPrimaryParty: RecordingCardPrimaryParty = forClientDetail
    ? "ambassador"
    : "client";

  const consolidatedViewerRole: ConsolidatedStatementViewerRole =
    forClientDetail ? "vendor" : "ambassador";

  const [recordings, setRecordings] = useState<any[] | undefined>();
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const recordingsListRef = useRef<unknown[]>([]);
  const viewerRoleRef = useRef<ConsolidatedStatementViewerRole>(
    consolidatedViewerRole
  );
  const pdfScopeRef = useRef<PdfScopeBlock | null | undefined>(pdfScope);

  useEffect(() => {
    viewerRoleRef.current = consolidatedViewerRole;
  }, [consolidatedViewerRole]);

  useEffect(() => {
    pdfScopeRef.current = pdfScope;
  }, [pdfScope]);

  useEffect(() => {
    const raw = recordings;
    recordingsListRef.current = Array.isArray(raw) ? raw : [];
  }, [recordings]);

  const load = useCallback(
    async (filters: MeetingRecordingsFiltersPayload) => {
      if (!lockedReady) return;

      const clientId = forClientDetail
        ? lockedClientId!
        : filters.participantId;
      const ambassadorId = forClientDetail
        ? filters.participantId
        : lockedAmbassadorId!;

      setLoading(true);
      setIsError(false);
      const res: any = await fetchMeetingRecordings({
        from: filters.from,
        to: filters.to,
        clientId: clientId || "",
        ambassadorId: ambassadorId || "",
      });
      setLoading(false);
      if (res.success) {
        setRecordings(Array.isArray(res.data) ? res.data : []);
      } else {
        setIsError(true);
        setRecordings(undefined);
      }
    },
    [lockedReady, forClientDetail, lockedClientId, lockedAmbassadorId]
  );

  useEffect(() => {
    load({ from: "", to: "", participantId: "" });
  }, [load]);

  const handleConsolidateStatement = () => {
    const list = recordingsListRef.current as unknown[];
    if (!list.length) {
      showNotification({
        message: "No recordings available to download.",
        type: "error",
      });
      return;
    }
    try {
      const blob = buildConsolidatedStatementPdfForList({
        recordings: list as any[],
        viewerRole: viewerRoleRef.current,
        pdfScope: pdfScopeRef.current ?? null,
      });
      saveAs(blob, `consolidated-statement_${list.length}-meetings.pdf`);
    } catch {
      showNotification({
        message:
          "Failed to generate consolidated summary. Please try again later.",
        type: "error",
      });
    }
  };

  return (
    <Box>
      <Stack gap={3}>
        <MeetingRecordingsFilters
          variant={filterVariant}
          lockedClientId={lockedClientId}
          lockedAmbassadorId={lockedAmbassadorId}
          onApply={(f) => load(f)}
          summaryAction={
            <Button
              type="button"
              variant="contained"
              size="medium"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleConsolidateStatement}
            >
              Consolidate summary
            </Button>
          }
        />
        <RecordingList
          fetching={loading}
          list={recordings}
          isError={isError}
          cardPrimaryParty={cardPrimaryParty}
        />
      </Stack>
    </Box>
  );
}
